import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Shield, Bug, Package, AlertTriangle, CheckCircle2,
  TrendingUp, BarChart3, Clock, Search, FileCode
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  getLiteSabreDashboard,
  getSeverityColor,
  getSeverityBadgeColor,
  formatScanType,
  formatDate
} from '@/services/litesabre';

const LiteSabreDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine base path from current location
  const basePath = location.pathname.startsWith('/litesabre') && !location.pathname.startsWith('/litesabre-pro') 
    ? '/litesabre' 
    : '/grc/litesabre';

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const data = await getLiteSabreDashboard();
      setDashboard(data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const severityOrder = ['critical', 'high', 'medium', 'low'];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-7 h-7 text-indigo-600" />
            LiteSabre Security Scanner
          </h1>
          <p className="text-gray-500 mt-1">
            Scan and prioritize vulnerabilities in code, dependencies, containers, and cloud configurations
          </p>
        </div>
        <Button onClick={() => navigate(`${basePath}/scan`)} className="bg-indigo-600 hover:bg-indigo-700">
          <Search className="w-4 h-4 mr-2" />
          New Scan
        </Button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Scans</p>
                <p className="text-3xl font-bold">{dashboard?.total_scans || 0}</p>
              </div>
              <div className="p-3 bg-indigo-100 rounded-full">
                <BarChart3 className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Vulnerabilities</p>
                <p className="text-3xl font-bold">{dashboard?.total_vulnerabilities || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <Bug className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Critical Issues</p>
                <p className="text-3xl font-bold">
                  {dashboard?.severity_distribution?.critical || 0}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Fix Rate</p>
                <p className="text-3xl font-bold">{dashboard?.fix_rate || 0}%</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Severity Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Severity Distribution</CardTitle>
            <CardDescription>Vulnerabilities by severity level</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {severityOrder.map(severity => {
                const count = dashboard?.severity_distribution?.[severity] || 0;
                const total = dashboard?.total_vulnerabilities || 1;
                const percentage = Math.round((count / total) * 100) || 0;
                
                return (
                  <div key={severity} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="capitalize font-medium">{severity}</span>
                      <span className="text-gray-500">{count} ({percentage}%)</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${getSeverityColor(severity).split(' ')[0]}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Scans by Type */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Scans by Type</CardTitle>
            <CardDescription>Distribution of scan types performed</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(dashboard?.scans_by_type || {}).map(([type, count]) => {
                const total = dashboard?.total_scans || 1;
                const percentage = Math.round((count / total) * 100) || 0;
                
                return (
                  <div key={type} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{formatScanType(type)}</span>
                      <span className="text-gray-500">{count} ({percentage}%)</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {Object.keys(dashboard?.scans_by_type || {}).length === 0 && (
                <p className="text-gray-400 text-center py-4">No scans performed yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Scans */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Recent Scans</CardTitle>
              <CardDescription>Latest vulnerability scans</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate(`${basePath}/scans`)}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboard?.recent_scans || []).slice(0, 5).map((scan, idx) => (
                <div
                  key={scan.id || idx}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition"
                  onClick={() => navigate(`${basePath}/scans/${scan.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded">
                      {scan.scan_type === 'dependency' && <Package className="w-4 h-4 text-blue-600" />}
                      {scan.scan_type === 'container' && <FileCode className="w-4 h-4 text-purple-600" />}
                      {scan.scan_type === 'iac' && <Shield className="w-4 h-4 text-green-600" />}
                    </div>
                    <div>
                      <p className="font-medium text-sm truncate max-w-[200px]">{scan.name}</p>
                      <p className="text-xs text-gray-500">{formatScanType(scan.scan_type)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 text-xs rounded-full ${scan.status === 'completed' ? 'bg-green-100 text-green-700' : scan.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {scan.status}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">
                      {scan.summary?.total_vulnerabilities || 0} vulns
                    </p>
                  </div>
                </div>
              ))}
              {(!dashboard?.recent_scans || dashboard.recent_scans.length === 0) && (
                <div className="text-center py-8 text-gray-400">
                  <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No scans yet. Start your first scan!</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => navigate(`${basePath}/scan`)}
                  >
                    Start Scan
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Vulnerable Packages */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top Vulnerable Packages</CardTitle>
            <CardDescription>Packages with most vulnerabilities</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboard?.top_vulnerable_packages || []).slice(0, 5).map((pkg, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded">
                      <Package className="w-4 h-4 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{pkg.package_name}</p>
                      <p className="text-xs text-gray-500">{pkg.ecosystem}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded border ${getSeverityBadgeColor(pkg.highest_severity)}`}>
                      {pkg.highest_severity}
                    </span>
                    <span className="text-sm font-medium text-gray-600">
                      {pkg.vulnerability_count} vulns
                    </span>
                  </div>
                </div>
              ))}
              {(!dashboard?.top_vulnerable_packages || dashboard.top_vulnerable_packages.length === 0) && (
                <p className="text-gray-400 text-center py-4">No vulnerable packages found</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button
              variant="outline"
              className="h-20 flex-col gap-2"
              onClick={() => navigate(`${basePath}/scan`)}
            >
              <Package className="w-5 h-5" />
              <span>Scan Dependencies</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex-col gap-2"
              onClick={() => navigate(`${basePath}/scan?type=repository`)}
            >
              <FileCode className="w-5 h-5" />
              <span>Scan Repository</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex-col gap-2"
              onClick={() => navigate(`${basePath}/scan?type=container`)}
            >
              <Shield className="w-5 h-5" />
              <span>Scan Container</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex-col gap-2"
              onClick={() => navigate(`${basePath}/vulnerabilities`)}
            >
              <Bug className="w-5 h-5" />
              <span>View Vulnerabilities</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex-col gap-2"
              onClick={() => navigate(`${basePath}/sbom`)}
              data-testid="sbom-reports-btn"
            >
              <FileCode className="w-5 h-5" />
              <span>SBOM Reports</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex-col gap-2"
              onClick={() => navigate(`${basePath}/trends`)}
              data-testid="scan-trends-btn"
            >
              <BarChart3 className="w-5 h-5" />
              <span>Scan Trends</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LiteSabreDashboard;
